/**
 * The wrong-connector redirect in the app (Warren, 29 Oct 2026): when the
 * driver started a plug that was Available while exactly one other plug on
 * the charger was Preparing with no session, the Api started that one instead
 * and says so in the start reply (connectorRedirect). Every screen with a
 * Start button (PreSessionPage, My Home, My Charger) tells the driver which
 * plug it started ("We started Plug 2, where your car is plugged in.") in
 * place of the plain started toast.
 *
 * Pure, so node tests drive it directly.
 */
import type { DriverStartResponse } from '@/lib/api';

export interface StartedPlugNotice {
  /** The plug the Api started, as the driver reads it on the unit. */
  plug: number;
}

/** The notice, or null for the plain started toast (no redirect, or an older Api). */
export function startedPlugNotice(
  started: Pick<DriverStartResponse, 'connectorId' | 'connectorRedirect'> | null | undefined,
): StartedPlugNotice | null {
  const redirect = started?.connectorRedirect;
  if (!redirect) return null;
  const { fromConnectorId, toConnectorId } = redirect;
  if (!Number.isInteger(toConnectorId) || toConnectorId <= 0 || toConnectorId === fromConnectorId) return null;
  // The reply's own connectorId, when present, must agree.
  if (typeof started?.connectorId === 'number' && started.connectorId !== toConnectorId) return null;
  return { plug: toConnectorId };
}

/** Seconds until the Api resends a start the charger Rejected while it held our earlier one, or null (Warren, 29 Oct
 *  2026). Whole seconds, at least 1, at most 600. */
export function startQueuedSeconds(started: Pick<DriverStartResponse, 'startQueued'> | null | undefined): number | null {
  const s = started?.startQueued?.waitSeconds;
  if (typeof s !== 'number' || !Number.isFinite(s) || s <= 0) return null;
  return Math.min(600, Math.max(1, Math.round(s)));
}

/** The other charger at the site the Api started, where the car is plugged in (wrong pedestal, Warren 29 Oct 2026). */
export interface StartedChargerNotice {
  /** The name on the unit (display name, else its identity). */
  name: string;
  /** The OCPP identity of the started charger, for the waiting screen's cancel. */
  identity: string;
}

/** The notice, or null when the Api started the tapped charger (or is older). */
export function startedChargerNotice(
  started: Pick<DriverStartResponse, 'chargerRedirect'> | null | undefined,
): StartedChargerNotice | null {
  const r = started?.chargerRedirect;
  if (!r || typeof r.toIdentity !== 'string' || !r.toIdentity || r.toIdentity === r.fromIdentity) return null;
  const name = typeof r.toName === 'string' && r.toName.trim() ? r.toName.trim() : r.toIdentity;
  return { name, identity: r.toIdentity };
}

/**
 * The Api's redirect shape (apps/api lib/startConnectorRedirect.ts
 * redirectCandidate), mirrored for the Start button: the tapped plug is
 * Available and exactly one other plug is Preparing. A live commissioned unit
 * would otherwise hold the tapped plug at "Plug in to start"; the Api starts
 * the Preparing plug instead, or answers PLUG_IN_FIRST itself when it keeps
 * the tapped one, so the button stays live.
 */
export function apiMayStartOtherPlug(
  connectors: ReadonlyArray<{ connector_id: number; status?: string | null }> | null | undefined,
  plug: number,
): boolean {
  // Connector 0 is the charger itself in OCPP 1.6, never a plug.
  const plugs = (connectors ?? []).filter((c) => c.connector_id > 0);
  if (plugs.length < 2) return false;
  if (plugs.find((c) => c.connector_id === plug)?.status !== 'Available') return false;
  return plugs.filter((c) => c.connector_id !== plug && c.status === 'Preparing').length === 1;
}
